/**
 * RBAC hooks - read from rbac slice (fed by RTK Query getEffectivePermissions).
 * Use these instead of any context; the query runs when authenticated and updates the slice.
 */
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useGetEffectivePermissionsQuery } from "../apis/rbac.api";
import {
  selectPermissionCodes,
  selectRbacHierarchy,
} from "../slices/rbacSlice";
import { sidebarMenuApi } from "../../services/sidebarMenuApi";
import { RoleTypeEnum } from "../../utils/constants";
import { useAuth } from "./useAuth";
import type { IEffectivePermissionHierarchyModule } from "../../types/rbac";

/**
 * Effective permissions and hierarchy from the rbac slice. Triggers the API
 * when authenticated (query updates the slice on success). Syncs hierarchy to sidebar cache.
 */
export function useEffectivePermissions(): {
  codes: Set<string>;
  hierarchy: IEffectivePermissionHierarchyModule[];
  isReady: boolean;
  reload: () => Promise<void>;
} {
  const { user, isAuthenticated } = useAuth();
  const { data, isLoading, isFetching, isUninitialized, refetch } =
    useGetEffectivePermissionsQuery(undefined, {
      skip: !isAuthenticated,
    });
  const permissionCodes = useSelector(selectPermissionCodes);
  const hierarchyFromState = useSelector(selectRbacHierarchy);

  const hierarchy = useMemo(() => {
    if (hierarchyFromState.length > 0) {
      return hierarchyFromState;
    }
    return data?.hierarchy ?? [];
  }, [hierarchyFromState, data?.hierarchy]);

  const codes = useMemo(() => {
    if (permissionCodes.length > 0) {
      return new Set(permissionCodes);
    }
    if (data?.permissions?.length) {
      return new Set(data.permissions.map((p) => p.code));
    }
    return new Set<string>();
  }, [permissionCodes, data?.permissions]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      sidebarMenuApi.invalidateCache(user?.id ?? "");
      return;
    }
    if (hierarchy.length) {
      sidebarMenuApi.setCache(user.id, hierarchy);
    }
  }, [isAuthenticated, user?.id, hierarchy]);

  const reload = useMemo(
    () => async () => {
      await refetch();
    },
    [refetch],
  );

  // Ready when we already have hierarchy/data in memory, or when the initial query settles.
  // Keeps isReady true during background refetches so layout does not unmount or reset state.
  const hasData = hierarchy.length > 0 || Boolean(data);
  const isReady =
    !isAuthenticated || hasData || (!isLoading && !isUninitialized && !isFetching);

  return {
    codes,
    hierarchy,
    isReady,
    reload,
  };
}

/**
 * True if the current user has the given permission code (or is SuperAdmin).
 */
export function useHasPermission(code: string): boolean {
  const { user, isAuthenticated } = useAuth();
  const { data } = useGetEffectivePermissionsQuery(undefined, { skip: !isAuthenticated });
  const permissionCodes = useSelector(selectPermissionCodes);

  return useMemo(() => {
    const u = user as { role?: string; isSuperAdmin?: boolean } | null;
    if (u?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase()) {
      return true;
    }
    if (permissionCodes.includes(code)) {
      return true;
    }
    return Boolean(data?.permissions?.some((p) => p.code === code));
  }, [user, permissionCodes, data?.permissions, code]);
}
